"use client";

import React from "react";
import { ChevronRight, OctagonAlert as AlertTriangle, CircleCheck as CheckCircle2, FileCheck2, CircleQuestionMark as HelpCircle, Info, Scale, ShieldAlert, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const providesList = [{
  title: "Preliminary Process Scoping",
  desc: "Identifies whether PAT, NIR, Raman, or powder testing is conceptually suitable for your material stream."
}, {
  title: "Technology Alignment",
  desc: "Suggests relevant analytical hardware and sampling geometries based on basic laboratory parameters."
}, {
  title: "Feasibility Consultation Planning",
  desc: "Structures key technical questions prior to submitting real samples for formal bench testing."
}];
const excludesList = [{
  title: "Not a Formal Regulatory Audit",
  desc: "Does not replace Annex 11, cGMP, or 21 CFR Part 11 validation protocols or compliance sign-offs."
}, {
  title: "Not a Certified IQ / OQ / PQ Protocol",
  desc: "Installation, operational, and performance qualifications must be executed on-site with certified reference standards."
}, {
  title: "Not a Guaranteed Analytical Assay",
  desc: "Spectral limits of detection and matrix interferences must be validated empirically in laboratory conditions."
}];
export default function AssessmentDisclaimer() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="assessmentdisclaimer" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="page-container">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} className="flex flex-col gap-12">
          {/* Breadcrumb Navigation */}
          <motion.div variants={itemVariants}>
            <nav aria-label="Breadcrumb">
              <ol className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm text-muted-foreground">
                <li className="inline-flex items-center gap-1.5">
                  <Link to="/HomePage" className="transition-colors hover:text-foreground">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true" className="text-muted-foreground/60">
                  <ChevronRight className="size-3.5" />
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Link to="/ResourcesPage" className="transition-colors hover:text-foreground">
                    Resources
                  </Link>
                </li>
                <li aria-hidden="true" className="text-muted-foreground/60">
                  <ChevronRight className="size-3.5" />
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <span className="font-medium text-foreground" aria-current="page">
                    Assess My Method
                  </span>
                </li>
              </ol>
            </nav>
          </motion.div>

          {/* Header Block */}
          <motion.div variants={itemVariants} className="max-w-3xl flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs uppercase tracking-wider font-semibold">
                <Info className="size-3.5" />
                Assessment Framework
              </Badge>
              <span className="text-xs text-muted-foreground font-mono">
                REF: AST-GUIDE-01
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
              About This Assessment & Regulatory Scope
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
              This self-check tool is an exploratory, application-led assessment designed to help laboratory,
              quality, and manufacturing teams across Ireland and the UK navigate suitable analytical options.
              It is an advisory tool, not an accredited certification.
            </p>
          </motion.div>

          {/* Primary Disclaimer Framing Alert */}
          <motion.div variants={itemVariants}>
            <Alert className="border-border bg-muted/40 p-6 sm:p-8 rounded-xl shadow-xs">
              <ShieldAlert className="size-6 text-primary mt-0.5 shrink-0" />
              <div className="col-start-2 flex flex-col gap-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <AlertTitle className="text-lg font-semibold text-foreground">
                    Legal & Compliance Disclaimer
                  </AlertTitle>
                  <span className="text-xs px-2.5 py-0.5 rounded-md bg-secondary text-secondary-foreground font-medium">
                    Self-Guided Evaluation Only
                  </span>
                </div>
                <AlertDescription className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  The outputs, recommendations, and tier categorizations generated by this self-check are provided
                  solely for informational scoping. Results do not constitute certified equipment qualification,
                  statutory validation, or binding engineering guarantees. Formal implementation in regulated
                  pharmaceutical, biopharmaceutical, or chemical environments requires structured feasibility
                  studies, chemometric model validation, and official qualification lifecycle services from Antech
                  Solutions specialists.
                </AlertDescription>
              </div>
            </Alert>
          </motion.div>

          {/* Two-Column Structured Scope Grid */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {/* What this provides */}
            <Card className="border border-border bg-card shadow-xs rounded-xl h-full flex flex-col">
              <CardHeader className="pb-4 border-b border-border">
                <div className="flex items-center gap-2.5">
                  <div className="size-9 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
                    <CheckCircle2 className="size-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-foreground">
                      What This Self-Check Provides
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Exploratory guidance for technical scoping
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 flex-1 flex flex-col justify-between gap-6">
                <div className="flex flex-col gap-4">
                  {providesList.map((item, i) => <div data-index={i} key={item.title} className="flex items-start gap-3">
                      <div className="size-2 rounded-full bg-primary mt-2 shrink-0" />
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">{item.title}</h3>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    </div>)}
                </div>

                <div className="bg-secondary/60 p-3.5 rounded-lg border border-border/70 flex items-center gap-3">
                  <FileCheck2 className="size-5 text-primary shrink-0" />
                  <p className="text-xs text-secondary-foreground">
                    Helps streamline your preparation before meeting with our application specialists.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* What this does NOT replace */}
            <Card className="border border-border bg-card shadow-xs rounded-xl h-full flex flex-col">
              <CardHeader className="pb-4 border-b border-border">
                <div className="flex items-center gap-2.5">
                  <div className="size-9 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
                    <Scale className="size-5 text-foreground" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-foreground">
                      What It Does Not Replace
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Formal validation requirements for regulated labs
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 flex-1 flex flex-col justify-between gap-6">
                <div className="flex flex-col gap-4">
                  {excludesList.map((item, i) => <div data-index={i} key={item.title} className="flex items-start gap-3">
                      <div className="size-2 rounded-full bg-muted-foreground mt-2 shrink-0" />
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">{item.title}</h3>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    </div>)}
                </div>

                <div className="bg-secondary/60 p-3.5 rounded-lg border border-border/70 flex items-center gap-3">
                  <AlertTriangle className="size-5 text-muted-foreground shrink-0" />
                  <p className="text-xs text-secondary-foreground">
                    Direct calibration and sample testing are essential prior to line integration.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Next Steps CTA Strip */}
          <motion.div variants={itemVariants} className="bg-secondary p-6 sm:p-8 rounded-xl border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex flex-col gap-1.5 max-w-xl">
              <h3 className="text-base sm:text-lg font-semibold text-secondary-foreground">
                Need Certified Qualification & On-Site Validation?
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Our application engineering team provides formal feasibility studies, chemometric modeling,
                and compliant lifecycle validation services across Ireland and the UK.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 shrink-0 w-full sm:w-auto">
              <Button asChild variant="default" className="w-full sm:w-auto">
                <Link to="/QualificationLifecycleServicesPage">
                  Qualification Services
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="/DiscussYourApplicationPage">
                  Discuss Your Application
                </Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}